import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';

export interface ScreenSize {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  orientation: 'portrait' | 'landscape';
  devicePixelRatio: number;
}

export interface BreakpointConfig {
  mobile: number;
  tablet: number;
  desktop: number;
}

@Injectable({
  providedIn: 'root'
})
export class ResponsiveService implements OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Default Bootstrap 5 breakpoints
  private breakpoints: BreakpointConfig = {
    mobile: 576,   // sm breakpoint
    tablet: 768,   // md breakpoint  
    desktop: 992   // lg breakpoint
  };

  // Screen size subject
  private screenSizeSubject = new BehaviorSubject<ScreenSize>(this.calculateScreenSize());
  
  // Public observables
  public screenSize$ = this.screenSizeSubject.asObservable();
  public isMobile$ = this.screenSize$.pipe(
    distinctUntilChanged((prev, curr) => prev.isMobile === curr.isMobile)
  );
  public isTablet$ = this.screenSize$.pipe(
    distinctUntilChanged((prev, curr) => prev.isTablet === curr.isTablet)
  );
  public isDesktop$ = this.screenSize$.pipe(
    distinctUntilChanged((prev, curr) => prev.isDesktop === curr.isDesktop)
  );
  public orientation$ = this.screenSize$.pipe(
    distinctUntilChanged((prev, curr) => prev.orientation === curr.orientation)
  );

  constructor() {
    this.setupResizeListener();
    this.setupOrientationListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupResizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(150), // Debounce resize events
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateScreenSize();
      });
  }

  private setupOrientationListener(): void {
    if ('screen' in window && 'orientation' in window.screen) {
      fromEvent(window.screen.orientation, 'change')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          // Wait a bit for the viewport to update after orientation change
          setTimeout(() => this.updateScreenSize(), 100);
        });
    }

    // Fallback for older browsers
    fromEvent(window, 'orientationchange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        setTimeout(() => this.updateScreenSize(), 100);
      });
  }

  private updateScreenSize(): void {
    const newSize = this.calculateScreenSize();
    this.screenSizeSubject.next(newSize);
  }

  private calculateScreenSize(): ScreenSize {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    return {
      width,
      height,
      isMobile: width < this.breakpoints.tablet,
      isTablet: width >= this.breakpoints.tablet && width < this.breakpoints.desktop,
      isDesktop: width >= this.breakpoints.desktop,
      orientation: width > height ? 'landscape' : 'portrait',
      devicePixelRatio: window.devicePixelRatio || 1
    };
  }

  // Utility methods
  getCurrentScreenSize(): ScreenSize {
    return this.screenSizeSubject.value;
  }

  isMobile(): boolean {
    return this.getCurrentScreenSize().isMobile;
  }

  isTablet(): boolean {
    return this.getCurrentScreenSize().isTablet;
  }

  isDesktop(): boolean {
    return this.getCurrentScreenSize().isDesktop;
  }

  isPortrait(): boolean {
    return this.getCurrentScreenSize().orientation === 'portrait';
  }

  isLandscape(): boolean {
    return this.getCurrentScreenSize().orientation === 'landscape';
  }

  getBreakpoint(): 'mobile' | 'tablet' | 'desktop' {
    const screen = this.getCurrentScreenSize();
    if (screen.isMobile) return 'mobile';
    if (screen.isTablet) return 'tablet';
    return 'desktop';
  }

  // Customize breakpoints if needed
  setBreakpoints(breakpoints: Partial<BreakpointConfig>): void {
    this.breakpoints = { ...this.breakpoints, ...breakpoints };
    this.updateScreenSize(); // Recalculate with new breakpoints
  }

  getBreakpoints(): BreakpointConfig {
    return { ...this.breakpoints };
  }

  // Touch device detection
  isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  // High DPI detection
  isHighDPI(): boolean {
    return this.getCurrentScreenSize().devicePixelRatio > 1;
  }

  // Safe area detection for mobile devices (e.g., iPhone X notch)
  getSafeAreaInsets(): { top: number; bottom: number; left: number; right: number } {
    const style = getComputedStyle(document.documentElement);
    return {
      top: parseInt(style.getPropertyValue('--safe-area-inset-top') || '0', 10),
      bottom: parseInt(style.getPropertyValue('--safe-area-inset-bottom') || '0', 10),
      left: parseInt(style.getPropertyValue('--safe-area-inset-left') || '0', 10),
      right: parseInt(style.getPropertyValue('--safe-area-inset-right') || '0', 10)
    };
  }

  // Viewport height helpers (useful for mobile browsers)
  getViewportHeight(): number {
    return window.innerHeight;
  }

  getDocumentHeight(): number {
    return Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
  }

  // Media query matching
  matchesMediaQuery(query: string): boolean {
    return window.matchMedia(query).matches;
  }

  // Preferred reduced motion detection
  prefersReducedMotion(): boolean {
    return this.matchesMediaQuery('(prefers-reduced-motion: reduce)');
  }

  // Dark mode preference detection
  prefersDarkMode(): boolean {
    return this.matchesMediaQuery('(prefers-color-scheme: dark)');
  }

  // High contrast preference detection
  prefersHighContrast(): boolean {
    return this.matchesMediaQuery('(prefers-contrast: high)');
  }

  // Create observable for custom media queries
  watchMediaQuery(query: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const mediaQuery = window.matchMedia(query);
      
      // Emit initial value
      observer.next(mediaQuery.matches);
      
      // Listen for changes
      const handler = (e: MediaQueryListEvent) => observer.next(e.matches);
      
      if (mediaQuery.addListener) {
        mediaQuery.addListener(handler);
        
        return () => {
          if (mediaQuery.removeListener) {
            mediaQuery.removeListener(handler);
          }
        };
      } else if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handler);
        
        return () => {
          mediaQuery.removeEventListener('change', handler);
        };
      }
      
      return () => {};
    }).pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    );
  }

  // Utility for responsive classes
  getResponsiveClasses(baseCss: string): string[] {
    const screen = this.getCurrentScreenSize();
    const classes = [baseCss];
    
    if (screen.isMobile) classes.push(`${baseCss}-mobile`);
    if (screen.isTablet) classes.push(`${baseCss}-tablet`);
    if (screen.isDesktop) classes.push(`${baseCss}-desktop`);
    if (screen.orientation === 'portrait') classes.push(`${baseCss}-portrait`);
    if (screen.orientation === 'landscape') classes.push(`${baseCss}-landscape`);
    
    return classes;
  }

  // Grid column calculator
  calculateColumns(totalColumns: number = 12): { mobile: number; tablet: number; desktop: number } {
    if (totalColumns <= 1) return { mobile: 12, tablet: 12, desktop: 12 };
    if (totalColumns <= 2) return { mobile: 12, tablet: 6, desktop: 6 };
    if (totalColumns <= 3) return { mobile: 12, tablet: 6, desktop: 4 };
    if (totalColumns <= 4) return { mobile: 12, tablet: 6, desktop: 3 };
    if (totalColumns <= 6) return { mobile: 12, tablet: 4, desktop: 2 };
    
    return { mobile: 12, tablet: 3, desktop: 1 };
  }

  // Scroll lock for mobile modals
  lockScroll(): void {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.width = '100%';
  }

  unlockScroll(): void {
    const scrollY = document.body.style.top;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  }
}